/* eslint-disable class-methods-use-this */
/* eslint-disable complexity */
import React from 'react';
import BaseClass from 'components/BaseClass';
import { wrapComponent } from 'utils/framework';
import { Link } from 'components/ui';
import ErrorConstants from 'utils/ErrorConstants';
import FormValidator from 'utils/FormValidator';
import creditCardUtils from 'utils/CreditCard';
import ErrorsUtils from 'utils/Errors';
import deepEqual from 'deep-equal';
import CVCInfoModal from 'components/FrictionlessCheckout/Payment/CVCInfoModal';
import agentAwareUtils from 'utils/AgentAware';
import CreditCardRow from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/CreditCardsList/CreditCardRow';
import FrictionlessUtils from 'utils/FrictionlessCheckout';

class CreditCardsList extends BaseClass {
    constructor(props) {
        super(props);
        this.state = {
            cvvValidated: false,
            showAllCards: false
        };
    }

    USERCCP = 'userccp';

    componentDidMount() {
        this.unsubscribe = this.props.CreditCardListToggleCVC(this);
        this.props.onRef(this);
    }

    validateCVV = () => {
        ErrorsUtils.collectClientFieldErrors([this.refs.securityCodeInput]);
        ErrorsUtils.validate();
        this.setState({ cvvValidated: true });
    };

    componentDidUpdate(prevProps) {
        if (prevProps.selectedCreditCardId !== this.props.selectedCreditCardId) {
            // Reset CVV validation when the selected card changes so the new card's CVV input gets validated
            this.setState({ cvvValidated: false });
        }

        if ((!this.state.cvvValidated && this.refs && this.refs.securityCodeInput) || !deepEqual(prevProps.orderDetails, this.props.orderDetails)) {
            this.validateCVV();
        }

        const giftCardNoLongerCoversOrder = prevProps.giftCardAmountEqualsOrderTotal && !this.props.giftCardAmountEqualsOrderTotal;

        if (giftCardNoLongerCoversOrder && !this.props.selectedCreditCardId && !this.props.alternateMethodSelected) {
            this.selectDefaultCreditCard();
        }
    }

    selectDefaultCreditCard = () => {
        const { cards, setNewCreditCard } = this.props;
        const selectedCard = FrictionlessUtils.findCardToAutoSelect(cards);

        if (selectedCard) {
            setNewCreditCard(selectedCard);
        }
    };

    componentWillUnmount() {
        this.unsubscribe();
    }

    showCVCInfoModal = () => {
        this.props.showCVCInfoModal(true);
    };

    handleValidateError = creditCard => code => {
        const { isZeroCheckout } = this.props;

        if (FormValidator.isEmpty(code)) {
            if (isZeroCheckout) {
                return ErrorConstants.ERROR_CODES.CREDIT_CARD_CVV_VALIDATION;
            }

            return ErrorConstants.ERROR_CODES.CREDIT_CARD_SECURITY_CODE;
        } else if (code.length < creditCardUtils.getSecurityCodeLength(creditCard?.cardType)) {
            return ErrorConstants.ERROR_CODES.CREDIT_CARD_SECURITY_CODE_LENGTH;
        }

        return null;
    };

    isUserCcpCard = card => {
        return card.creditCardId?.startsWith(this.USERCCP);
    };

    getCardKey = card => {
        return `${card?.cardType}-${card?.cardNumber}-${card?.expirationMonth}-${card?.expirationYear}`;
    };

    removeDuplicatedCards = cards => {
        const map = new Map();

        cards.forEach(card => {
            const key = this.getCardKey(card);

            if (!map.has(key)) {
                map.set(key, card);

                return;
            }

            const existing = map.get(key);

            // Prefer userccp card (non temporary cards)
            if (!this.isUserCcpCard(existing) && this.isUserCcpCard(card)) {
                map.set(key, card);
            }
        });

        return Array.from(map.values());
    };

    /**
     * Sorts cards to move the currently active (selected) card to the top of the list.
     * This ensures that when user re-enters L2 Edit State, their previously selected card
     * appears first, making it easier to confirm their selection.
     */
    sortCardsByActiveFirst = cards => {
        const { creditCardPaymentGroup } = this.props;
        const activeCreditCardId = creditCardPaymentGroup?.creditCardId;

        // No active card selected, return cards in original order
        if (!activeCreditCardId) {
            return cards;
        }

        const activeCardIndex = cards.findIndex(card => card.creditCardId === activeCreditCardId);

        // Card not found or already first, no sorting needed
        if (activeCardIndex === -1 || activeCardIndex === 0) {
            return cards;
        }

        // Move active card to first position, keep others in original order
        const activeCard = cards[activeCardIndex];
        const remainingCards = [...cards.slice(0, activeCardIndex), ...cards.slice(activeCardIndex + 1)];

        return [activeCard, ...remainingCards];
    };

    handleUpdateSecurityCode = e => {
        const { updateSecurityCode } = this.props;
        updateSecurityCode(FormValidator.replaceDotSeparator(e.target.value, this.refs.securityCodeInput));
    };

    handleToggleShowAll = () => {
        this.setState(prevState => ({ showAllCards: !prevState.showAllCards }));
    };

    render() {
        const {
            cards,
            totalCardsCount,
            defaultPayment,
            selectedCreditCardId,
            shouldDisplayAddOrDeleteCreditCardButton,
            locales,
            showEditButtonFirst,
            onUseThisCard,
            onSetNewCreditCard,
            onShowEditCreditCardForm,
            onRemoveCreditCard,
            onDeleteCreditCard,
            hasDefaultAlternatePayment,
            disabled
        } = this.props;

        const { showAllCards } = this.state;

        const { showAll, showLess } = locales;

        const creditCardIsDefaultPayment = !defaultPayment || defaultPayment === 'creditCard';

        let showEditRemoveButtons = true;

        if (agentAwareUtils.isAgent) {
            showEditRemoveButtons = agentAwareUtils.isAgentAuthorized(['3']);
        }

        const uniqueCards = this.removeDuplicatedCards(cards);
        // Sort cards to show active card first when re-entering L2 Edit State
        const sortedCards = this.sortCardsByActiveFirst(uniqueCards);
        // totalCardsCount represents the full card count including any cards hoisted out by the parent.
        // The toggle threshold is checked against the total so visibility matches the user's mental model
        // of "how many cards do I have", regardless of which ones the parent rendered separately.
        // Use the un-deduped cards.length here so dedup inside this component doesn't inflate hoistedCount.
        const totalCount = totalCardsCount ?? cards.length;
        const hoistedCount = totalCount - cards.length;
        const showAllThreshold = hasDefaultAlternatePayment ? 2 : 3;
        const shouldShowToggle = totalCount > showAllThreshold;
        // Cap visible cards so the combined visible count (hoisted + this list) stays at the threshold when collapsed.
        const visibleSlice = Math.max(0, showAllThreshold - hoistedCount);
        const visibleCards = showAllCards || !shouldShowToggle ? sortedCards : sortedCards.slice(0, visibleSlice);

        return (
            <div>
                {visibleCards.map((creditCard, index) => {
                    const isLastVisible = index === visibleCards.length - 1;
                    const isLastInFullList = index === cards.length - 1;
                    const showDivider = !shouldShowToggle ? !isLastVisible : showAllCards ? !isLastInFullList : index !== visibleSlice - 1;

                    return (
                        <CreditCardRow
                            key={creditCard.creditCardId}
                            creditCard={creditCard}
                            showDivider={showDivider}
                            selectedCreditCardId={selectedCreditCardId}
                            disabled={disabled}
                            creditCardIsDefaultPayment={creditCardIsDefaultPayment}
                            showEditRemoveButtons={showEditRemoveButtons}
                            showEditButtonFirst={showEditButtonFirst}
                            shouldDisplayAddOrDeleteCreditCardButton={shouldDisplayAddOrDeleteCreditCardButton}
                            locales={locales}
                            onSetNewCreditCard={onSetNewCreditCard}
                            onShowEditCreditCardForm={onShowEditCreditCardForm}
                            onRemoveCreditCard={onRemoveCreditCard}
                            onDeleteCreditCard={onDeleteCreditCard}
                            onUseThisCard={onUseThisCard}
                        />
                    );
                })}
                {shouldShowToggle && (
                    <>
                        <Link
                            color='blue'
                            display='inline-block'
                            marginTop={4}
                            data-at={Sephora.debug.dataAt('show_all_credit_cards')}
                            onClick={this.handleToggleShowAll}
                        >
                            {showAllCards ? showLess : showAll}
                        </Link>
                    </>
                )}
                <CVCInfoModal />
            </div>
        );
    }
}

export default wrapComponent(CreditCardsList, 'CreditCardsList');
